import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle,
  ArrowRight,
  Target,
  Users,
  Filter,
  Calendar,
  Shield,
  Menu,
  X
} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Button } from '../components/ui/button';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_270b8cc9-1693-407c-b15e-5414d2b83f2e/artifacts/qe9vta95_LOGO-FIRMA-PNG-1-branco-258x278-1%20%282%29.png";

// Aggressive variant of the medical-practices landing page, rendered in the Firma
// monochrome design system (same tokens/classes as the root LandingPage). Served at
// /medical-practices. All CTAs route into the existing booking funnel (/book).
export default function MedicalPracticesPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) element.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  const goToBooking = () => {
    setMobileMenuOpen(false);
    navigate('/book', { state: { from: '/medical-practices' } });
  };

  const navItems = [
    { label: 'The System', id: 'system' },
    { label: 'Results', id: 'results' },
    { label: 'What You Get', id: 'includes' },
    { label: 'FAQs', id: 'faq' },
  ];

  const trustItems = [
    '90+ practices served',
    '25,000+ appointments booked',
    'Results in 30 days or free',
  ];

  const guaranteeMilestones = [
    'Week 2: First qualified leads arriving in your pipeline.',
    'Week 4: First booked patient appointments from the system.',
    'Day 90: A measurable, documented increase in monthly private-pay patient volume.',
  ];

  const painPoints = [
    {
      bold: 'Referrals are random, not reliable.',
      text: 'You spent years building your clinical reputation — but new patients come in with no pattern and no control.'
    },
    {
      bold: 'Ads gave you price-shoppers and no-shows.',
      text: "You tried advertising before and got people who want to use insurance you don't accept."
    },
    {
      bold: "You're cutting fees just to fill the calendar.",
      text: 'That just means more work for less margin — and it still doesn\'t stick.'
    },
    {
      bold: 'Competitors with worse outcomes are fully booked.',
      text: 'Your best appointment slots sit empty while they run the same cookie-cutter ads.'
    }
  ];

  const methodSteps = [
    {
      icon: Target,
      step: '01',
      title: 'We Find Patients Ready to Pay',
      description: 'Precision paid ads on Google and Meta targeting patients in your area who are actively searching your specialty and can pay out-of-pocket.'
    },
    {
      icon: Users,
      step: '02',
      title: 'We Convert Clicks Into Consultations',
      description: 'A custom landing page for your top 3 services captures intent and turns ad clicks into booked consultation requests within minutes.'
    },
    {
      icon: Filter,
      step: '03',
      title: 'We Qualify Before You Spend a Minute',
      description: 'Every lead goes through an automated pre-qualification sequence via SMS and email. By the time they reach you, they match your ideal patient profile.'
    },
    {
      icon: Calendar,
      step: '04',
      title: 'Your Calendar Fills Itself',
      description: 'Patients self-schedule directly into your calendar through an automated booking system. You arrive to work — the appointments are already there.'
    }
  ];

  const caseStudies = [
    {
      initials: 'LG',
      number: '63',
      result: '+63 Patients / Month',
      author: 'Dr. Lucas Guerreiro',
      practice: 'Guerreiro Dermatology',
      quote: 'We went from chasing referrals to having a waitlist. I can finally focus on patient care instead of wondering where the next appointment is coming from.'
    },
    {
      initials: 'JC',
      number: '87',
      result: '+87 Patients / Month',
      author: 'Dr. James Chen',
      practice: 'Plastic Surgery',
      quote: 'Our schedule is full three weeks ahead. Best marketing decision I\'ve made in 15 years of practice — the ROI is undeniable.'
    },
    {
      initials: 'BR',
      number: '112',
      result: '$100K in 2 Months',
      author: 'Dr. Beatriz Rodriguez',
      practice: 'MedSpa',
      quote: 'Since partnering in May, I consistently received 70-80 quality leads each month, leading to nearly $100,000 in just two months — simply the best!'
    }
  ];

  const offerIncludes = [
    'Custom paid ad campaigns on Google and Meta — built for your specialty and your market',
    'High-converting landing pages for your top 3 services',
    'Automated pre-qualification system: SMS + email filtering leads before they reach you',
    'Calendar automation — patients self-book directly into your scheduling system',
    'Dedicated growth strategist — senior operator, not a junior account manager',
    'Weekly performance reports: cost per lead, cost per booked appointment, ROI'
  ];

  const offerExcludes = [
    'No long-term contracts — month-to-month with 30-day notice',
    'No insurance-dependent patients — private-pay only',
    'No junior account managers on your account',
    'No vague "brand awareness" deliverables',
    'No shared leads — only one practice per specialty per market'
  ];

  const faqs = [
    {
      question: 'How fast can I expect results?',
      answer: 'Most practices see their first qualified leads within 14 days of launch. First booked appointments typically arrive in week 3–4. Our 90-day guarantee backs this up — if you don\'t see a measurable increase, we keep working at no charge.'
    },
    {
      question: 'How much does it cost?',
      answer: 'Investment depends on your market size and goals — we cover this in full on the strategy call. There\'s no obligation on the call and we won\'t pressure you into anything that doesn\'t make sense for your practice.'
    },
    {
      question: 'Do I need a big ad budget?',
      answer: 'No. We\'ve seen strong results with practices starting at $2,000–$3,500/month in ad spend. Your media budget goes directly to the platforms — we never mark it up.'
    },
    {
      question: 'What if I\'ve tried ads before and got burned?',
      answer: 'Most failed ad campaigns had the same problem: generic agencies running generic ads with no qualification system. Our system pre-qualifies every lead before they ever reach your calendar. That\'s the difference.'
    },
    {
      question: 'Do you work with multiple practices in the same area?',
      answer: 'No. We work with only one practice per specialty per market. That exclusivity is the foundation of the results we deliver.'
    }
  ];

  // Decorative monochrome calendar mock (visual only — routes to /book on interaction).
  const calRows = [
    ['', '', '1', '2', '3', '4', '5'],
    ['6', '7', '8', '9', '10', '11', '12'],
    ['13', '14', '15', '16', '17', '18', { n: '19', active: true }],
    [{ n: '20', avail: true }, { n: '21', avail: true }, { n: '22', avail: true }, '23', '24', '25', '26'],
  ];
  const calSlots = ['11:00 AM', '11:15 AM', '04:00 PM', '04:15 PM', '06:00 PM', '06:15 PM'];

  return (
    <div className="min-h-screen bg-white">
      {/* Top availability bar */}
      <div className="bg-[#0A0A0A] py-2.5">
        <p className="text-center text-xs sm:text-sm text-white font-light px-4" data-testid="availability-bar">
          <span className="font-medium">Limited availability:</span> We only onboard 3 new practices per month — Applications open now
        </p>
      </div>

      {/* Header */}
      <header data-testid="header" className="section-light sticky top-0 z-40 border-b border-[#E5E7EB] bg-white/95 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center gap-3" data-testid="logo">
              <img src={LOGO_URL} alt="Firma Medical Growth" className="h-12 w-auto invert" />
              <span className="hidden sm:block text-sm font-medium text-[#0A0A0A]">
                Medical Growth Systems
              </span>
            </div>

            <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="text-sm font-medium text-[#6B7280] hover:text-[#0A0A0A] transition-colors"
                  data-testid={`nav-${item.id}`}
                >
                  {item.label}
                </button>
              ))}
            </nav>

            <div className="hidden lg:block">
              <Button onClick={goToBooking} className="btn-primary" data-testid="header-cta">
                Book My Free Call
              </Button>
            </div>

            <button
              className="lg:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        <div className="bg-[#0A0A0A] py-2">
          <p className="text-center text-xs text-white font-light" data-testid="exclusivity-badge">
            Only 1 clinic per specialty per market — no internal competition.
          </p>
        </div>
      </header>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="mobile-menu lg:hidden" data-testid="mobile-menu">
          <div className="flex justify-between items-center mb-8">
            <img src={LOGO_URL} alt="Firma" className="h-10 w-auto" />
            <button onClick={() => setMobileMenuOpen(false)} className="relative z-[60] p-2" data-testid="mobile-menu-close">
              <X className="w-6 h-6 text-white" />
            </button>
          </div>
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => scrollToSection(item.id)}
              className="mobile-menu-item block w-full text-left"
              data-testid={`mobile-nav-${item.id}`}
            >
              {item.label}
            </button>
          ))}
          <Button onClick={goToBooking} className="btn-primary-inverse w-full mt-8">
            Book My Free Call
          </Button>
        </div>
      )}

      {/* Hero Section */}
      <section data-testid="hero-section" className="section-light py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm font-medium uppercase tracking-wider text-[#6B7280] mb-5" data-testid="hero-preheadline">
            Attention: Private Practice Owners
          </p>

          <h1 className="heading-xl mb-6" data-testid="hero-headline">
            Get 25–50 New Private-Pay Patients Every Month — Or You Don't Pay.
          </h1>

          <p className="body-lg text-[#6B7280] mb-8 max-w-3xl mx-auto" data-testid="hero-subheadline">
            The Patient Growth System fills your calendar with pre-qualified, cash-pay patients — without insurance networks, referrals, or cold outreach. Week after week, on autopilot.
          </p>

          <div className="flex flex-col items-center">
            <Button onClick={goToBooking} className="btn-primary flex items-center gap-2" data-testid="hero-cta">
              Book My Free Patient Growth Call
              <ArrowRight className="w-5 h-5" />
            </Button>
            <p className="text-sm text-[#6B7280] mt-4" data-testid="hero-subtext">
              Free 30-min session. No contracts. No obligation.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-x-8 gap-y-3 mt-10 pt-8 border-t border-[#E5E7EB]" data-testid="hero-trust">
            {trustItems.map((item, index) => (
              <div key={index} className="flex items-center gap-2 text-sm text-[#6B7280]">
                <CheckCircle className="w-5 h-5 text-[#0A0A0A] flex-shrink-0" />
                {item}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Calendar / Scheduling Section */}
      <section data-testid="calendar-section" className="section-light py-16 lg:py-20 bg-[#F9FAFB] border-y border-[#E5E7EB]">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm font-medium uppercase tracking-wider text-[#6B7280] mb-3">
            Step 1 of 2 — Pick Your Time
          </p>
          <h2 className="heading-md mb-8" data-testid="calendar-headline">
            Strategy Call · Zoom · 45 min
          </h2>

          <button
            onClick={goToBooking}
            className="block w-full max-w-xl mx-auto bg-white border border-[#E5E7EB] p-6 text-left transition-all hover:border-[#0A0A0A]"
            data-testid="calendar-mock"
          >
            <div className="flex items-center gap-3 pb-4 mb-4 border-b border-[#E5E7EB]">
              <span className="w-2.5 h-2.5 rounded-full bg-[#0A0A0A]" />
              <div>
                <p className="font-medium text-sm text-[#0A0A0A]">Medical Growth Systems</p>
                <p className="text-xs text-[#6B7280]">Free Patient Growth Strategy Call</p>
              </div>
            </div>

            <div className="grid grid-cols-7 gap-1 mb-3">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
                <div key={d} className="text-[11px] text-center text-[#9CA3AF] py-1">{d}</div>
              ))}
              {calRows.flat().map((cell, i) => {
                const obj = typeof cell === 'object' ? cell : { n: cell };
                return (
                  <div
                    key={i}
                    className={[
                      'text-[13px] text-center py-1.5',
                      obj.active ? 'bg-[#0A0A0A] text-white rounded-full font-bold' : '',
                      obj.avail ? 'bg-[#F3F4F6] text-[#0A0A0A] font-medium' : '',
                      !obj.active && !obj.avail ? 'text-[#6B7280]' : '',
                    ].join(' ')}
                  >
                    {obj.n}
                  </div>
                );
              })}
            </div>

            <div className="flex flex-wrap gap-2 mt-4">
              {calSlots.map((slot) => (
                <span key={slot} className="border border-[#0A0A0A] text-[#0A0A0A] text-xs font-medium px-3.5 py-1.5">
                  {slot}
                </span>
              ))}
            </div>
          </button>
        </div>
      </section>

      {/* Guarantee Section */}
      <section data-testid="guarantee-section" className="section-dark noise-overlay py-24 lg:py-32">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Shield className="w-16 h-16 mx-auto mb-6" />
          <span className="badge badge-dark mb-6">30-Day Guarantee</span>
          <h2 className="heading-lg mb-6 mt-6" data-testid="guarantee-headline">
            30 New Patients. Or You Don't Pay.
          </h2>
          <p className="body-lg text-[#9CA3AF] mb-10">
            We're so confident in the Patient Growth System that we back every engagement with a clear commitment. If we don't hit all three milestones, we keep working at no additional charge until we do. No fine print. No asterisks. That's the deal.
          </p>
          <div className="text-left max-w-lg mx-auto mb-10">
            <ul className="space-y-4">
              {guaranteeMilestones.map((milestone, index) => (
                <li key={index} className="flex items-start gap-3 text-[#E5E7EB]" data-testid={`guarantee-milestone-${index}`}>
                  <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5 text-white" />
                  <span className="body-md">{milestone}</span>
                </li>
              ))}
            </ul>
          </div>
          <Button onClick={goToBooking} className="btn-primary-inverse flex items-center gap-2 mx-auto" data-testid="guarantee-cta">
            Claim Your Free Strategy Call
            <ArrowRight className="w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Pain Identification Section */}
      <section data-testid="pain-section" className="section-light py-24 lg:py-32">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="heading-lg text-center mb-4" data-testid="pain-headline">
            The Truth About Growing a Private Practice in 2026
          </h2>
          <p className="body-lg text-[#6B7280] text-center mb-12 max-w-xl mx-auto">
            Nobody wants to hear it — but if you run a private practice, you already know this feeling:
          </p>

          <div className="space-y-4" data-testid="pain-points">
            {painPoints.map((point, index) => (
              <div key={index} className="flex items-start gap-4 bg-[#F9FAFB] border-l-2 border-[#0A0A0A] p-5" data-testid={`pain-point-${index}`}>
                <X className="w-5 h-5 text-[#0A0A0A] flex-shrink-0 mt-0.5" />
                <p className="body-sm text-[#374151]">
                  <span className="block font-semibold text-[#0A0A0A] mb-1">{point.bold}</span>
                  {point.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote Bar */}
      <section className="section-dark py-16 lg:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xl sm:text-2xl font-light italic leading-relaxed text-[#9CA3AF]" data-testid="quote-bar">
            "Every empty slot isn't just lost revenue.{' '}
            <span className="text-white not-italic font-medium">It's a patient you didn't help — and a competitor who did.</span>{' '}
            The problem isn't your clinical skills. The problem is your patient acquisition system."
          </p>
        </div>
      </section>

      {/* Method/System Section */}
      <section id="system" data-testid="method-section" className="section-light py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="heading-lg text-center mb-4" data-testid="method-headline">
            The Patient Growth System: 4 Steps That Fill Your Calendar
          </h2>
          <p className="body-lg text-[#6B7280] text-center mb-16">
            A done-for-you paid acquisition engine that runs on autopilot.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="method-steps">
            {methodSteps.map((step, index) => (
              <div key={index} className="step-card" data-testid={`method-step-${index}`}>
                <span className="text-6xl font-bold text-[#E5E7EB]">{step.step}</span>
                <step.icon className="w-8 h-8 mt-4 mb-4" />
                <h3 className="heading-sm mb-3">{step.title}</h3>
                <p className="body-sm text-[#6B7280]">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section id="results" data-testid="results-section" className="section-dark noise-overlay py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="heading-lg text-center mb-4" data-testid="results-headline">
            90+ Medical Practices. One System. These Are Their Numbers.
          </h2>
          <p className="body-lg text-[#9CA3AF] text-center mb-16">
            Real practices. Real results. Documented.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12" data-testid="stat-cards">
            {caseStudies.map((study, index) => (
              <div key={index} className="case-card text-center" data-testid={`stat-card-${index}`}>
                <p className="text-7xl font-bold text-white leading-none mb-2">{study.number}</p>
                <p className="text-xs text-[#9CA3AF] uppercase tracking-wider">booked appts in 30 days</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="testimonials">
            {caseStudies.map((study, index) => (
              <div key={index} className="case-card text-left" data-testid={`testimonial-${index}`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 rounded-full bg-white text-[#0A0A0A] flex items-center justify-center font-bold text-sm flex-shrink-0">
                    {study.initials}
                  </div>
                  <div>
                    <p className="font-medium text-white text-sm">{study.author}</p>
                    <p className="text-xs text-[#9CA3AF]">{study.practice}</p>
                  </div>
                </div>
                <span className="badge badge-dark mb-4">{study.result}</span>
                <p className="text-sm italic text-[#E5E7EB] leading-relaxed mt-4">"{study.quote}"</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Offer / Includes Section */}
      <section id="includes" data-testid="offer-section" className="section-light py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="heading-lg text-center mb-16" data-testid="offer-headline">
            Here's Exactly What You Get
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="offer-card" data-testid="offer-includes">
              <h3 className="heading-sm mb-6">What's Included</h3>
              <ul className="space-y-4">
                {offerIncludes.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#0A0A0A] flex-shrink-0 mt-0.5" />
                    <span className="body-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="offer-card flex flex-col" data-testid="offer-excludes">
              <h3 className="heading-sm mb-6">What You Won't Deal With</h3>
              <ul className="space-y-4 mb-8">
                {offerExcludes.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <X className="w-5 h-5 text-[#0A0A0A] flex-shrink-0 mt-0.5" />
                    <span className="body-sm">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-auto pt-2">
                <Button onClick={goToBooking} className="btn-primary w-full" data-testid="offer-cta">
                  Book My Free Strategy Call
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" data-testid="faq-section" className="section-dark noise-overlay py-24 lg:py-32">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="heading-lg text-center mb-16" data-testid="faq-headline">
            Frequently Asked Questions
          </h2>

          <Accordion type="single" collapsible className="space-y-2" data-testid="faq-accordion">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="border-b border-[#1F2937]">
                <AccordionTrigger
                  className="text-left text-lg font-medium text-white hover:text-[#9CA3AF] py-6"
                  data-testid={`faq-trigger-${index}`}
                >
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-[#9CA3AF] pb-6 leading-relaxed" data-testid={`faq-content-${index}`}>
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA Section */}
      <section id="cta" data-testid="cta-section" className="section-dark py-24 lg:py-32">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="heading-lg mb-6" data-testid="cta-headline">
            Ready to Stop Guessing Where Your Next Patient Is Coming From?
          </h2>
          <p className="body-lg text-[#9CA3AF] mb-10">
            Book a free 30-minute strategy call. We'll audit your market, show you exactly what the Patient Growth System looks like for your specialty, and give you a clear picture of what's possible in your first 90 days.
          </p>

          <div className="flex flex-col items-center">
            <Button onClick={goToBooking} className="btn-primary-inverse flex items-center gap-2" data-testid="cta-button">
              Book My Free Strategy Call — Limited Spots This Week
              <ArrowRight className="w-5 h-5" />
            </Button>
            <p className="text-sm text-[#6B7280] mt-4">
              We only take 3 new medical practices per week to ensure full attention and results.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer data-testid="footer" className="section-dark py-16 border-t border-[#1F2937]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Firma" className="h-10 w-auto" />
              <span className="text-sm text-[#9CA3AF]">Medical Growth Systems</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-[#9CA3AF]">
              <a href="#" className="hover:text-white transition-colors" data-testid="footer-privacy">Privacy Policy</a>
              <a href="#" className="hover:text-white transition-colors" data-testid="footer-terms">Terms</a>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-[#1F2937] text-center">
            <p className="text-xs text-[#6B7280] mb-4" data-testid="footer-exclusivity">
              Patient Exclusivity Policy: We work with only one practice per specialty per market.
            </p>
            <p className="text-xs text-[#6B7280]">
              © {new Date().getFullYear()} Firma Medical Growth Systems. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
